import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, ChevronDown, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { announcement, navMenu, company } from "../mock";
import { useAuth } from "../context/AuthContext";
import { useUI } from "../context/UIContext";

import { tr } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
const Logo = () => (
  <Link to="/" className="flex items-center gap-2 shrink-0 group">
    <span className="font-display text-2xl md:text-[28px] leading-none tracking-tight text-white">
      {company.brandMark}
    </span>
    <span className="flex flex-col leading-none">
      <span className="text-[10px] tracking-[0.35em] text-[#c2a15a] font-medium">
        {company.brandSub}
      </span>
      <span className="text-[9px] tracking-[0.2em] text-white/50">
        {tr("LAWYERS LLP")}
      </span>
    </span>
  </Link>
);

export default function Header({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { user } = useAuth();
  const { openIllustration, openLogin } = useUI();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#" + id);
    }
  };

  const opaque = solid || scrolled;

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      {/* Announcement bar */}
      <div className="bg-[#c2a15a] text-[#0b1f3a]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-9 flex items-center justify-center gap-3 text-[12px] md:text-[13px] font-medium">
          <span className="hidden sm:inline">{announcement.text}</span>
          <span className="sm:hidden truncate">{tr("Banking admin, solved.")}</span>
          <button
            onClick={openIllustration}
            className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 underline underline-offset-2 hover:opacity-70 transition-opacity"
          >
            {announcement.cta} <ArrowUpRight size={13} />
          </button>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`transition-all duration-300 ${
          opaque
            ? "bg-[#0b1f3a]/95 backdrop-blur-md shadow-lg shadow-black/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 h-[70px] flex items-center justify-between gap-6">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-5 whitespace-nowrap">
            {navMenu.map((item) =>
              item.items ? (
                <div key={item.label} className="relative group">
                  <button className="flex items-center gap-1 text-[14px] text-white/85 hover:text-white transition-colors py-6">
                    {item.label}
                    <ChevronDown
                      size={15}
                      className="transition-transform group-hover:rotate-180"
                    />
                  </button>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                    <div className="bg-white rounded-xl shadow-2xl ring-1 ring-[#c2a15a]/20 p-2 w-60">
                      {item.items.map((sub) => (
                        <button
                          key={sub.to}
                          onClick={() => navigate(sub.to)}
                          className="w-full text-left px-4 py-2.5 rounded-lg text-[14px] text-[#0b1f3a] hover:bg-[#f6f2e9] transition-colors"
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={item.label}
                  onClick={() =>
                    item.anchor ? goAnchor(item.anchor) : navigate(item.to)
                  }
                  className="text-[14px] text-white/85 hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              )
            )}
          </nav>

          {/* Right actions */}
          <div className="hidden xl:flex items-center gap-4 whitespace-nowrap">
            <LanguageSwitcher testid="language-switcher-desktop" />
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="text-[13px] text-white/80 hover:text-[#c2a15a] transition-colors uppercase tracking-wide"
              >
                {tr("My dashboard")}
              </button>
            ) : (
              <button
                onClick={() => openLogin("client")}
                data-testid="header-login-button"
                className="text-[13px] text-white/80 hover:text-[#c2a15a] border border-white/20 hover:border-[#c2a15a] rounded-full px-4 h-9 transition-colors uppercase tracking-wide"
              >
                {tr("Login")}
              </button>
            )}
          </div>

          {/* Mobile */}
          <div className="xl:hidden flex items-center gap-2">
            <LanguageSwitcher testid="language-switcher-mobile" />
            <Sheet open={openMenu} onOpenChange={setOpenMenu}>
              <SheetTrigger asChild>
                <button className="text-white p-2" aria-label={tr("Menu")}>
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-[#0b1f3a] border-l border-[#c2a15a]/20 w-[300px] p-0"
              >
                <div className="p-6 flex flex-col h-full overflow-y-auto">
                  <div className="mb-8">
                    <Logo />
                  </div>
                  <div className="flex flex-col gap-1">
                    {navMenu.map((item) =>
                      item.items ? (
                        <div key={item.label} className="py-2">
                          <p className="text-[#c2a15a] text-[12px] uppercase tracking-widest mb-2">
                            {item.label}
                          </p>
                          {item.items.map((sub) => (
                            <button
                              key={sub.to}
                              onClick={() => {
                                setOpenMenu(false);
                                navigate(sub.to);
                              }}
                              className="block w-full text-left text-white/85 py-2 pl-3 text-[15px]"
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <button
                          key={item.label}
                          onClick={() => {
                            setOpenMenu(false);
                            if (item.anchor) setTimeout(() => goAnchor(item.anchor), 250);
                            else navigate(item.to);
                          }}
                          className="text-left text-white/90 py-3 text-[16px] border-b border-white/10"
                        >
                          {item.label}
                        </button>
                      )
                    )}
                  </div>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {user ? (
                      <button
                        onClick={() => {
                          setOpenMenu(false);
                          navigate("/dashboard");
                        }}
                        className="col-span-2 border border-white/25 text-white uppercase tracking-wide text-[13px] py-2.5 rounded-full"
                      >
                        {tr("My dashboard")}
                      </button>
                    ) : (
                      <>
                        <button data-testid="mobile-client-login-button"
                          onClick={() => {
                            setOpenMenu(false);
                            openLogin("client");
                          }}
                          className="border border-white/25 text-white uppercase tracking-wide text-[13px] py-2.5 rounded-full"
                        >
                          {tr("Client login")}
                        </button>
                        <button data-testid="mobile-partner-login-button"
                          onClick={() => {
                            setOpenMenu(false);
                            openLogin("partner");
                          }}
                          className="border border-white/25 text-white uppercase tracking-wide text-[13px] py-2.5 rounded-full"
                        >
                          {tr("Partner login")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </div>
  );
}
